import GIF from 'gif.js'
import type { GifExportConfig, ExportProgress, ExportResult } from './types'
import { DEFAULT_GIF_EXPORT_CONFIG, generateFilename, normalizeGifConfig } from './types'
import type { EffectsState } from '../effects/types'
import type { FullRenderSettings } from '../types'
import { ExportPipeline } from './ExportPipeline'
import { createFrameRenderer } from './FrameRenderer'
import { GifJsEncoder } from './encoders/GifJsEncoder'
import type { GifExportJob } from './jobs/GifExportJob'

/**
 * GIF exporter with offline frame rendering and gif.js encoding.
 */
export class GifExporter {
  private config: GifExportConfig
  private gif: GIF | null = null
  private capturing: boolean = false
  private encoding: boolean = false
  private currentProgress: number = 0
  private frameCount: number = 0
  private totalFrames: number = 0
  private canvasWidth: number = 0
  private canvasHeight: number = 0
  private activeJob: GifExportJob | null = null
  private readonly pipeline: ExportPipeline

  private progressCallback?: (progress: ExportProgress) => void
  private completeCallback?: (result: ExportResult) => void

  constructor() {
    this.config = DEFAULT_GIF_EXPORT_CONFIG
    this.pipeline = new ExportPipeline({
      frameRenderer: createFrameRenderer(),
    })
  }

  /**
   * Offline GIF export using deterministic frame rendering.
   */
  async export(
    sourceImage: HTMLImageElement | HTMLCanvasElement | ImageData,
    settings: Partial<FullRenderSettings> = {},
    effectsState?: EffectsState,
    config: Partial<GifExportConfig> = {}
  ): Promise<ExportResult> {
    this.config = { ...DEFAULT_GIF_EXPORT_CONFIG, ...config }
    this.capturing = false
    this.encoding = false
    this.currentProgress = 0
    this.frameCount = 0

    const normalizedConfig = normalizeGifConfig(this.config, {
      width: this.getSourceWidth(sourceImage),
      height: this.getSourceHeight(sourceImage),
    })
    this.totalFrames = normalizedConfig.totalFrames
    this.canvasWidth = normalizedConfig.width
    this.canvasHeight = normalizedConfig.height

    const encoder = new GifJsEncoder((progress) => {
      this.currentProgress = progress.progress
      this.encoding = progress.status === 'encoding'
      this.progressCallback?.(progress)
    })
    const job = this.pipeline.createGifJob(
      normalizedConfig,
      {
        sourceImage,
        settings,
        effectsState,
      },
      encoder
    )

    this.activeJob = job
    const resultPromise = new Promise<ExportResult>((resolve) => {
      job.onProgress((progress) => {
        this.currentProgress = progress.progress
        this.frameCount = progress.currentFrame ?? this.frameCount
        this.capturing = progress.status === 'preparing' || progress.status === 'rendering_frames'
        this.encoding = progress.status === 'encoding'
        this.progressCallback?.(progress)
      })

      job.onComplete((result) => {
        this.activeJob = null
        this.capturing = false
        this.encoding = false
        this.currentProgress = result.success ? 100 : this.currentProgress
        this.completeCallback?.(result)
        resolve(result)
      })
    })

    void job.start()
    return resultPromise
  }

  init(config: Partial<GifExportConfig> = {}): void {
    this.config = { ...DEFAULT_GIF_EXPORT_CONFIG, ...config }
    this.capturing = false
    this.encoding = false
    this.currentProgress = 0
    this.frameCount = 0
    this.totalFrames = this.config.fps * this.config.duration

    // Canvas size is set when the first frame is added.
  }

  private initGifEncoder(): void {
    if (this.gif) {
      this.gif.abort()
    }

    this.gif = new GIF({
      workers: 2,
      quality: this.config.quality,
      width: this.canvasWidth,
      height: this.canvasHeight,
      workerScript: `${import.meta.env.BASE_URL}gif.worker.js`,
      repeat: this.config.loop,
    })

    // Encoding progress callback.
    this.gif.on('progress', (progress: number) => {
      const encodingProgress = Math.round(50 + progress * 50)
      this.currentProgress = encodingProgress
      this.progressCallback?.({
        status: 'encoding',
        progress: encodingProgress,
        currentFrame: this.frameCount,
        totalFrames: this.totalFrames,
        message: `GIF encoding ${Math.round(progress * 100)}%`,
      })
    })

    // Completion callback.
    this.gif.on('finished', (blob: Blob) => {
      this.encoding = false
      this.currentProgress = 100

      this.progressCallback?.({
        status: 'completed',
        progress: 100,
        message: 'GIF export completed',
      })

      const filename = generateFilename('gif')
      this.completeCallback?.({
        success: true,
        filename,
        size: blob.size,
        blob,
        timestamp: Date.now(),
      })
    })
  }

  setCanvasSize(width: number, height: number): void {
    this.canvasWidth = Math.round(width * this.config.scale)
    this.canvasHeight = Math.round(height * this.config.scale)
    this.initGifEncoder()
  }

  /**
   * Start frame capture.
   */
  startCapturing(): void {
    this.capturing = true
    this.frameCount = 0
    this.currentProgress = 0
    this.canvasWidth = 0
    this.canvasHeight = 0

    this.progressCallback?.({
      status: 'capturing',
      progress: 0,
      currentFrame: 0,
      totalFrames: this.totalFrames,
      message: 'Start capturing frames...',
    })
  }

  /**
   * Add a captured frame.
   */
  addFrame(canvas: HTMLCanvasElement): void {
    if (this.frameCount === 0 && this.capturing) {
      this.setCanvasSize(canvas.width, canvas.height)
    }

    if (!this.gif || !this.capturing) {
      console.warn('GifExporter.addFrame: gif not initialized or not capturing', {
        hasGif: !!this.gif,
        capturing: this.capturing,
        frameCount: this.frameCount,
      })
      return
    }

    this.frameCount++

    const delay = Math.round(1000 / this.config.fps)

    if (this.config.scale !== 1) {
      const scaledCanvas = this.scaleCanvas(canvas)
      this.gif.addFrame(scaledCanvas, { delay, copy: true })
    } else {
      this.gif.addFrame(canvas, { delay, copy: true })
    }

    const captureProgress = Math.round((this.frameCount / this.totalFrames) * 50)
    this.currentProgress = captureProgress

    this.progressCallback?.({
      status: 'capturing',
      progress: captureProgress,
      currentFrame: this.frameCount,
      totalFrames: this.totalFrames,
      message: `Captured frame ${this.frameCount}/${this.totalFrames}`,
    })

    if (this.frameCount >= this.totalFrames) {
      this.render()
    }
  }

  /**
   * Scale a canvas for export.
   */
  private scaleCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
    const scaled = document.createElement('canvas')
    scaled.width = this.canvasWidth
    scaled.height = this.canvasHeight

    const ctx = scaled.getContext('2d')!
    ctx.imageSmoothingEnabled = false
    ctx.drawImage(source, 0, 0, scaled.width, scaled.height)

    return scaled
  }

  /**
   * Start GIF encoding.
   */
  render(): void {
    if (!this.gif) return

    this.capturing = false
    this.encoding = true

    this.progressCallback?.({
      status: 'encoding',
      progress: 50,
      currentFrame: this.frameCount,
      totalFrames: this.totalFrames,
      message: 'Start GIF encoding...',
    })

    this.gif.render()
  }

  /**
   * Cancel export.
   */
  cancel(): void {
    if (this.activeJob) {
      void this.activeJob.cancel()
      this.activeJob = null
    }

    if (this.gif) {
      this.gif.abort()
      this.gif = null
    }

    this.capturing = false
    this.encoding = false
    this.currentProgress = 0
    this.frameCount = 0
    this.canvasWidth = 0
    this.canvasHeight = 0

    this.progressCallback?.({
      status: 'idle',
      progress: 0,
      message: 'Canceled',
    })
  }

  isCapturing(): boolean {
    return this.capturing
  }

  isEncoding(): boolean {
    return this.encoding
  }

  isProcessing(): boolean {
    return this.capturing || this.encoding
  }

  getProgress(): number {
    return this.currentProgress
  }

  getFrameCount(): number {
    return this.frameCount
  }

  getTotalFrames(): number {
    return this.totalFrames
  }

  onProgress(callback: (progress: ExportProgress) => void): void {
    this.progressCallback = callback
  }

  onComplete(callback: (result: ExportResult) => void): void {
    this.completeCallback = callback
  }

  destroy(): void {
    this.cancel()
    this.progressCallback = undefined
    this.completeCallback = undefined
  }

  private getSourceWidth(source: HTMLImageElement | HTMLCanvasElement | ImageData): number {
    return 'naturalWidth' in source
      ? source.naturalWidth
      : 'width' in source
        ? source.width
        : 0
  }

  private getSourceHeight(source: HTMLImageElement | HTMLCanvasElement | ImageData): number {
    return 'naturalHeight' in source
      ? source.naturalHeight
      : 'height' in source
        ? source.height
        : 0
  }
}


export function createGifExporter(): GifExporter {
  return new GifExporter()
}
